'use client';

import { useMemo } from 'react';
import { useJobQueueStore } from '@/features/design/job-queue';
import { useActiveUpscaleForRender } from '@/features/design/job-queue';
import { RenderStatus } from '@/shared/constants';
import { isRenderInQueue } from '@/shared/lib/render-status';
import { isUserVisibleRenderType } from '@/types/render';
import type { Render } from '@/types/render';
import type { Job } from '@/features/design/job-queue';
import { useRendersByAngle } from '../model/renders-cache';

export interface AngleRendersData {
  // All renders for this angle from Store
  allRenders: Render[];

  // Completed renders with files (for display)
  completedRenders: Render[];

  // Current render (selected by index or last one)
  currentRender: Render | undefined;
  currentRenderIndex: number;

  // Active render job (PROCESSING/PENDING, excludes upscales)
  activeRenderJob: Job | undefined;
  isRendering: boolean;

  // Active upscale job for current render
  activeUpscaleJob: Job | undefined;
  isUpscaling: boolean;

  // Helper flags
  hasCompletedRenders: boolean;
  totalRenders: number;
}

export interface UseAngleRendersDataOptions {
  conceptId: string | undefined;
  angleId: string | undefined;
  selectedRenderId?: string; // Optional: which render to show by ID (defaults to last)
}

/**
 * Centralized hook for angle renders data
 *
 * Provides:
 * - All renders for angle (from Store Map - reactive to upscale updates)
 * - Active render job (excludes upscales)
 * - Active upscale job for current render
 * - Helper computed values
 *
 * Use this instead of duplicating logic in MainView/SecondaryViews
 */
export function useAngleRendersData({
  conceptId,
  angleId,
  selectedRenderId,
}: UseAngleRendersDataOptions): AngleRendersData {
  // Renders are owned by TanStack Query (`cacheKeys.renders.list(conceptId)`).
  // The hook returns ASC-sorted list; flip to DESC here.
  const ascRenders = useRendersByAngle(conceptId, angleId);
  const jobQueue = useJobQueueStore((state) => state.jobQueue);

  const allRenders = useMemo(() => {
    if (!angleId) return [];
    return [...ascRenders].sort(
      (a, b) =>
        new Date(b.attributes.created_at).getTime() -
        new Date(a.attributes.created_at).getTime()
    );
  }, [ascRenders, angleId]);

  // Filter completed renders with files (exclude intermediate renders)
  const completedRenders = useMemo(() => {
    return allRenders.filter(
      (r) =>
        r.attributes.status === RenderStatus.COMPLETED &&
        r.relationships?.file?.meta?.url &&
        // Only show user-visible render types (excludes intermediate_photorealistic)
        isUserVisibleRenderType(r.attributes.render_type)
    );
  }, [allRenders]);

  const hasCompletedRenders = completedRenders.length > 0;
  const totalRenders = completedRenders.length;

  // Determine current render by ID. `completedRenders` is sorted DESC by
  // created_at (newest first), so `[0]` is the newest — that's the right
  // fallback when no selection exists. Refine submission reads `currentRender`
  // for `parent_render_id`; using the newest avoids accidentally refining
  // off the oldest render in a derivative stack.
  const currentRender = useMemo(() => {
    if (selectedRenderId) {
      return (
        completedRenders.find((r) => r.id === selectedRenderId) ||
        completedRenders[0]
      );
    }
    return completedRenders[0];
  }, [completedRenders, selectedRenderId]);

  // Calculate current index for backwards compatibility
  const currentIdx = currentRender
    ? completedRenders.findIndex((r) => r.id === currentRender.id)
    : 0;

  // Find active render job (PROCESSING/PENDING, excludes upscales)
  const activeRenderJob = useMemo(() => {
    if (!angleId) return undefined;
    return jobQueue.find(
      (j) =>
        j.conceptAngleId === angleId &&
        j.jobType === 'render' && // Exclude upscales - they have separate progress
        isRenderInQueue(j.status)
    );
  }, [jobQueue, angleId]);

  const isRendering = !!activeRenderJob;

  // Get active upscale for current render
  const activeUpscaleJob = useActiveUpscaleForRender(currentRender?.id);
  const isUpscaling = !!activeUpscaleJob;

  return {
    allRenders,
    completedRenders,
    currentRender,
    currentRenderIndex: currentIdx,
    activeRenderJob,
    isRendering,
    activeUpscaleJob,
    isUpscaling,
    hasCompletedRenders,
    totalRenders,
  };
}
